import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useClientConfig from "../hooks/clientConfigHook";
import AddressInputAU, { toString as toStringAU } from "./AddressInput_AU";
import AddressInputVU, { toString as toStringVU } from "./AddressInput_VU";
import CountryInput, { toString as toStringCountry } from "./CountryInput";
import { Col, Row } from "./Grid";
import Label from "./Label";
import TextInput from "./TextInput";
import PropTypes from 'prop-types';

export const toString = ({ value, defaultValue, clientConfig, ...props }) => {
    if (!value) {
        return defaultValue;
    }
    const country = value?.country ?? clientConfig.address.defaultCountry;
    const countryStr = toStringCountry({ value: country, defaultValue, clientConfig, ...props });
    switch (value?.country) {
        case 'AU':
            return toStringAU({ value, defaultValue, ...props }) + ' ' + countryStr;
        case 'VU':
            return toStringVU({ value, defaultValue, ...props }) + ' ' + countryStr;
        default:
            return value?.address1 + ' ' + countryStr;
    }
}

const AddressInputIntl = ({ address, live, placeholder, hideLabel = false, onChange, readonly, required, disabled : disabledInput, ...props }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const disabled = useCallback((field) => {
        if (typeof disabledInput === 'function') {
            return disabledInput(field);
        } else {
            return !!disabledInput;
        }
    }, [disabledInput])

    const readonlyPlaceholder = useMemo(() => placeholder?.default ?? t('address'), [placeholder?.default, t]);
    const addressString = useMemo(() => address?.address1 + ' ' + address?.country, [address]);
    const country = address?.country ?? clientConfig?.address?.defaultCountry;
    const isSpecialized = ['AU', 'VU'].includes(country);

    const handleChange = useCallback((field, value) => {
        if (!address?.country && field !== 'country') {
            onChange('country', clientConfig?.address?.defaultCountry);
        }
        onChange(field, value);
    }, [onChange, address?.country, clientConfig?.address?.defaultCountry]);

    if (readonly && !isSpecialized) {
        return <Label placeholder={readonlyPlaceholder} hideLabel={hideLabel} text={addressString} {...props}></Label>
    }
    return <>
        <Row>
            <Col>
            { country === 'AU' && <AddressInputAU address={address} live={live} placeholder={placeholder} hideLabel={hideLabel} onChange={handleChange} readonly={readonly} required={required} disabled={disabledInput} {...props}></AddressInputAU>}
            { country === 'VU' && <AddressInputVU address={address} live={live} placeholder={placeholder} hideLabel={hideLabel} onChange={handleChange} readonly={readonly} required={required} disabled={disabledInput} {...props}></AddressInputVU>}
            { !isSpecialized && <TextInput live={live} maxLength={256} placeholder={placeholder?.address1 ?? t('address')} value={address?.adress1} hideLabel={hideLabel} onChnage={a => handleChange('address1', a)} required={required} disabled={disabled('address1')} {...props}></TextInput>}
            </Col>
        </Row>
        <Row>
            <Col>
                <CountryInput live={live} value={country} placeholder={placeholder?.country || t('country')} onChange={entry => handleChange('country', entry)} readonly={readonly} required={required} disabled={disabled('country')} {...props}></CountryInput>
            </Col>
        </Row>
    </>
}
AddressInputIntl.propTypes = {
    /**
     * The object in which the address fields should be stored
     */
    address: PropTypes.object,

    /**
     * Set to true for every key stroke to call the onChange function
     */
    live: PropTypes.bool,

    /**
     * A map of placeholders for each of the address components
     */
    placeholder: PropTypes.oneOfType([PropTypes.objectOf(PropTypes.string), PropTypes.oneOf([''])]),  

    /**
     * Copllapse label spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    readonly: PropTypes.bool,

    /**
     * A function called whenever an address field is updated.
     * The function takes a field name and the new value to be assigned to that field.
     */
    onChange: PropTypes.func
}

export default AddressInputIntl;